import { CSSProperties, useEffect, useRef, useState } from 'react'
import styles from './PptPage.module.less'
import { commonUtils } from '@/helpers/commonUtils'
import cls from 'classnames'
import dayjs from 'dayjs'
import { SliderModels } from '@/types/slider'

interface Props {
  children: React.ReactNode
  bgSrc: string
  no: number
  onPageRender: (slider: SliderModels.Slider, pageRect: DOMRect) => void
}

function PptPage({ bgSrc, children, no, onPageRender }: Props) {
  const WIDTH = 1920
  const HEIGHT = 1080
  const pageRef = useRef<HTMLDivElement>(null)
  const onRender = (elements: SliderModels.PPTElement[]) => {
    const rect = pageRef.current!.getBoundingClientRect()
    const _elements = elements.map((element) => {
      const x = element.x - rect.left
      const y = element.y - rect.top
      return { ...element, x, y }
    })
    const slider: SliderModels.Slider = {
      background: bgSrc,
      elements: _elements,
    }
    onPageRender(slider, rect)
  }
  return (
    <div
      className={styles.pptPage}
      ref={pageRef}
      style={{
        width: WIDTH,
        minHeight: HEIGHT,
        maxHeight: HEIGHT,
        height: HEIGHT,
      }}
    >
      <img className={styles.bg} src={bgSrc} alt='' />
      <div className={styles.content}>{children}</div>
      <BottomLine no={no} onRender={onRender} />
    </div>
  )
}

function BottomLine(props: {
  className?: string
  style?: CSSProperties
  no: number
  onRender: (elements: SliderModels.PPTElement[]) => void
}) {
  const imgRef = useRef<HTMLImageElement>(null)
  const noRef = useRef<HTMLSpanElement>(null)
  const lineRef = useRef<HTMLSpanElement>(null)
  const noStr = props.no >= 10 ? props.no.toString() : `0${props.no}`
  const handleLoaded = () => {
    /**
     * 因为 moment analysis 这页 ppt 会动态计算文本的高度，
     * 然后缩小字号。bottom line 仅仅计算的是第一次的高度，
     * 缩小字号后的位置没有重新计算，所以导致 ppt 中 bottom line
     * 的位置不正确，所以这里设置一个延时，应该会在文本高度计算之后
     * 在进行位置计算
    */
    setTimeout(() => {
      const imgRect = imgRef.current!.getBoundingClientRect()
      const noRect = noRef.current!.getBoundingClientRect()
      const lineRect = lineRef.current!.getBoundingClientRect()
      const imgElement: SliderModels.PPTImageElement = {
        type: 'image',
        src: commonUtils.getImagePath('PDFDownload/divider-line.png'),
        width: imgRect.width,
        height: imgRect.height,
        x: imgRect.left,
        y: imgRect.top,
      }
      const lineElement: SliderModels.PPTLineElement = {
        type: 'line',
        start: [0, 0],
        end: [lineRect.width, 0],
        style: 'solid',
        color: '#515072',
        x: lineRect.left,
        y: lineRect.top,
        width: lineRect.height,
        points: ['', ''],
      }
      const noElement: SliderModels.PPTTextElement = {
        type: 'text',
        content: noRef.current!.outerHTML,
        width: noRect.width,
        height: noRect.height,
        x: noRect.left,
        y: noRect.top - 50,
      }
      props.onRender([imgElement, lineElement, noElement])
    }, 300)
  }
  return (
    <div className={cls(styles.bottomLine, props.className)} style={props.style}>
      <img
        ref={imgRef}
        className={styles.pic}
        src={commonUtils.getImagePath('PDFDownload/divider-line.png')}
        alt=''
        onLoad={handleLoaded}
      />
      <span className={styles.line} ref={lineRef}></span>
      <span className={styles.no}>
        <span ref={noRef} style={{ fontSize: '28px', lineHeight: '45px', color: '#333333' }}>
          {noStr}
        </span>
      </span>
    </div>
  )
}

export function PageHeader(props: {
  project: ProjectModels.ProjectDetail
  text?: string
  onRender: (elements: SliderModels.PPTElement[]) => void
  titleOffsetX?: number
}) {
  const text = useRef<HTMLSpanElement>(null)
  const underline = useRef<HTMLImageElement>(null)
  const [timestampElements, setTimestampElements] = useState<SliderModels.PPTElement[]>([])
  const [brandElements, setBrandElements] = useState<SliderModels.PPTElement[]>([])

  const handleTimeStampRender = (elements: SliderModels.PPTElement[]) => {
    setTimestampElements(elements)
  }
  const handleBrandRender = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const imgRect = e.currentTarget.getBoundingClientRect()
    const elements: SliderModels.PPTElement[] = [
      {
        type: 'image',
        src: commonUtils.getImagePath('PDFDownload/PDF-logo.png'),
        width: imgRect.width,
        height: imgRect.height,
        x: imgRect.left,
        y: imgRect.top,
      },
    ]
    setBrandElements(elements)
  }

  useEffect(() => {
    if (!timestampElements.length || (!props.text && !brandElements.length)) {
      return
    }
    const elements: SliderModels.PPTElement[] = [...timestampElements]
    if (props.text) {
      const textRect = text.current!.getBoundingClientRect()
      const underlineRect = underline.current!.getBoundingClientRect()
      elements.push({
        type: 'text',
        content: text.current!.outerHTML,
        width: textRect.width,
        height: textRect.height,
        x: textRect.left,
        y: textRect.top - 45,
      })
      elements.push({
        type: 'image',
        src: commonUtils.getImagePath('PPTDownload/page-header-underline.png'),
        width: underlineRect.width,
        height: underlineRect.height,
        x: underlineRect.left - 10,
        y: underlineRect.top,
      })
    } else {
      elements.push(...brandElements)
    }
    props.onRender(elements)
  }, [timestampElements, brandElements])

  return (
    <div className={styles.pageHeader}>
      {props.text ? (
        <span className={styles.pageTitle} style={{ marginLeft: props.titleOffsetX ?? 20 }}>
          <span
            ref={text}
            style={{ fontWeight: 'bold', fontSize: 40, color: '#2A2C3F', lineHeight: '56px' }}
          >
            {props.text}
          </span>
          <img
            className={styles.underline}
            ref={underline}
            src={commonUtils.getImagePath('PPTDownload/page-header-underline.png')}
          />
        </span>
      ) : (
        <img
          className={styles.brand}
          src={commonUtils.getImagePath('PDFDownload/PDF-logo.png')}
          alt=''
          onLoad={handleBrandRender}
        />
      )}
      <Timestamp project={props.project} onRender={handleTimeStampRender} />
    </div>
  )
}

export function Timestamp(props: {
  project: ProjectModels.ProjectDetail
  onRender: (elements: SliderModels.PPTElement[]) => void
}) {
  const box = useRef<HTMLSpanElement>(null)
  const text = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    if (!box.current || !text.current) {
      return
    }
    const textRect = text.current.getBoundingClientRect()
    const boxRect = box.current.getBoundingClientRect()
    const elements: SliderModels.PPTElement[] = [
      {
        type: 'shape',
        width: boxRect.width,
        height: boxRect.height,
        x: boxRect.left,
        y: boxRect.top,
        viewBox: [200, 200],
        path: 'M 0 0 L 200 0 L 200 200 L 0 200 Z',
        fill: 'rgba(255, 255, 255, 0)',
        outline: {
          width: 1,
          color: '#000',
          style: 'solid',
        },
        fixedRatio: false,
      },
      {
        type: 'text',
        content: text.current?.outerHTML,
        width: textRect.width,
        height: textRect.height,
        x: textRect.left,
        y: textRect.top - 50,
      },
    ]
    props.onRender(elements)
  }, [])
  return (
    <span
      className={styles.timestamp}
      ref={box}
      style={{
        padding: '12px 20px',
        borderRadius: '2px',
        border: '1px solid #2A2C3F',
      }}
    >
      <span
        ref={text}
        style={{
          fontWeight: 'bold',
          fontSize: '20px',
          color: '#333',
        }}
      >
        {dayjs(props.project.createdAt * 1000).format('YYYY-MM-DD')}
      </span>
    </span>
  )
}

export function PeriodBox(props: { period: string; onReady: (elements: SliderModels.PPTElement[]) => void }) {
  const box = useRef<HTMLDivElement>(null)
  const text = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    if (!box.current || !text.current) {
      return
    }
    const boxRect = box.current.getBoundingClientRect()
    const textRect = text.current.getBoundingClientRect()
    const SHAPE_RATIO = 0.5
    const SHAPE_WIDTH = boxRect.width
    const SHAPE_HEIGHT = boxRect.height
    const SHAPE_HEIGHT_RATIO = SHAPE_HEIGHT * SHAPE_RATIO
    const SHAPE_WIDTH_DELTA = SHAPE_WIDTH - SHAPE_HEIGHT_RATIO
    const SHAPE_HEIGHT_DELTA = SHAPE_HEIGHT - SHAPE_HEIGHT_RATIO
    const elements: SliderModels.PPTElement[] = [
      {
        type: 'shape',
        width: boxRect.width,
        height: boxRect.height,
        x: boxRect.left - 10,
        y: boxRect.top,
        fill: '#515366',
        fixedRatio: false,
        viewBox: [boxRect.width, boxRect.height],
        path: `M ${SHAPE_HEIGHT_RATIO} 0 L ${SHAPE_WIDTH_DELTA} 0 Q ${SHAPE_WIDTH} 0 ${SHAPE_WIDTH} ${SHAPE_HEIGHT_RATIO} L ${SHAPE_WIDTH} ${SHAPE_HEIGHT_DELTA} Q ${SHAPE_WIDTH} ${SHAPE_HEIGHT} ${SHAPE_WIDTH_DELTA} ${SHAPE_HEIGHT} L ${SHAPE_HEIGHT_RATIO} ${SHAPE_HEIGHT} Q 0 ${SHAPE_HEIGHT} 0 ${SHAPE_HEIGHT_DELTA} L 0 ${SHAPE_HEIGHT_RATIO} Q 0 0 ${SHAPE_HEIGHT_RATIO} 0 Z`,
        rotate: 0,
        pathFormula: SliderModels.ShapePathFormulasKeys.ROUND_RECT,
        keypoints: [SHAPE_RATIO],
      },
      {
        type: 'text',
        content: text.current.outerHTML,
        width: textRect.width,
        height: textRect.height,
        x: textRect.left,
        y: textRect.top - 50,
      }
    ]
    // console.log('elements', elements)
    props.onReady(elements)
  }, [])
  return (
    <div className={styles.periodBox} ref={box}>
      <span style={{ fontWeight: 'bold', fontSize: 40, color: '#FFFEFF' }} ref={text}>
        {props.period}
      </span>
    </div>
  )
}

export default PptPage
